import { type FC, useEffect, useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { skipToken } from '@reduxjs/toolkit/query'
import * as Sentry from '@sentry/react'
import { useIntercom } from 'react-use-intercom'
import { Spinner } from '../../../components/Spinner/Spinner'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { addToast } from '../../../store/toast/toastSlice'
import {
  useCreateBookingMutation,
  useCreateUserSchedulingLocationMutation,
  useGetAccountSchedulingSettingsQuery,
  useGetBookingByIdQuery,
  useGetSchedulingEventTypesQuery,
  useGetSchedulingLocationsQuery,
  useGetUserSchedulingLocationsQuery,
  useGetUserSchedulingSettingsQuery,
  useUpdateBookingMutation,
} from '../../../store/scheduling/scheduling'
import { type CreateBookingPayload } from '../../../store/scheduling/dto'
import { useGetExternalUserByIdQuery } from '../../../store/externalUser/externalUser'
import {
  type Booking,
  type BookingClient,
  DeliveryMode,
  EmailTemplatePurpose,
  type Location,
  LocationKind,
  MeetingMode,
  type SchedulingEventType,
} from '../../../models/Scheduling'
import { type User } from '../../../models/User'
import { OwnerSelect } from './form/OwnerSelect'
import { EventTypeSelect } from './form/EventTypeSelect'
import { ClientSearch } from './form/ClientSearch'
import { InviteOthers } from './form/InviteOthers'
import { LocationSelect } from './form/LocationSelect'
import { SetTimeSection } from './form/SetTimeSection'
import { MeetingNameField } from './form/MeetingNameField'
import {
  bookingLocationFields,
  canManageOwnLocations,
  hasBookableCalendar,
  isMeetingPlatformBroken,
  resolveLocationPreviewId,
  resolveViewTimezone,
  suggestMeetingTitle,
} from './form/scheduleHelpers'
import { MeetingPlatformWarning } from '../shared/MeetingPlatformWarning'
import { SchedulePreviewColumn } from './SchedulePreviewColumn'
import { useCopyRenderedEmail } from './useCopyRenderedEmail'
import { noDraftRecipientsMessage, usePushBookingToDraft } from './usePushBookingToDraft'
import { SchedulingFlowHeader } from './SchedulingFlowHeader'
import { ScheduleSaveBar } from './ScheduleSaveBar'
import { STALE_TITLE, errorNotice, saveBarNotices, type SaveBarNotice } from './saveBarNotice'
import { useLinkBaseline } from './useLinkBaseline'
import { useLinkShareState } from './useLinkShareState'
import { buildEmailTemplateContext } from './buildEmailTemplateContext'
import { renderEmailHtmlBody, renderEmailSubject } from './renderEmailTemplate'
import { useSchedulingEmailTemplates } from '../shared/useSchedulingEmailTemplates'
import { useGetAccountQuery } from '../../../store/accountUserSettings/accountUserSettings'
import { formatTimezoneLabel } from '../shared/schedulingDisplay'
import { clientInviteUrl, collectClientInviteLinks, formatClientInviteLinks, withDraftInviteUrls } from './clientInviteLinks'
import { useUserTimezone } from '../../../common/useUserTimezone'
import { scheduleFormSchema, type ScheduleFormValues } from './scheduleFormSchema'
import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { toLocalISODate } from '../../../common/dateUtils'
import { parseISODateAsLocal } from '../bookingFormat'

const FORM_ID = 'schedule-meeting-form'

const upcomingSuggestedDay = (isoDate: string | undefined): Date | undefined => {
  if (!isoDate) {
    return undefined
  }
  const day = parseISODateAsLocal(isoDate)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return day >= today ? day : undefined
}

type IncompleteReasonInput = Pick<ScheduleFormValues, 'owner' | 'eventType' | 'clients' | 'timeMode' | 'slot'> & {
  isOwnerCalendarBookable: boolean
  isBookingForOther: boolean
  ownerName: string
}

const getIncompleteReason = ({
  owner,
  eventType,
  clients,
  timeMode,
  slot,
  isOwnerCalendarBookable,
  isBookingForOther,
  ownerName,
}: IncompleteReasonInput): string | undefined => {
  if (owner == null) {
    return 'Select an owner'
  }
  if (eventType == null) {
    return 'Select an event type'
  }
  if (clients.length === 0) {
    return 'Add at least one client'
  }
  if (timeMode === 'specific-time' && slot == null) {
    return 'Pick a time'
  }
  if (!isOwnerCalendarBookable) {
    return isBookingForOther ? `${ownerName}'s calendar isn't ready for booking yet.` : 'Connect or reconnect your calendar to book meetings.'
  }
  return undefined
}

interface ScheduleMeetingFormProps {
  prefillEventType?: SchedulingEventType
  accountLocations: Location[]
  accountDefaultLocationId?: string | null
  allowCustomLocation: boolean
  opportunity?: Booking
  prefillClient?: BookingClient
}

const ScheduleMeetingForm: FC<ScheduleMeetingFormProps> = ({
  prefillEventType,
  accountLocations,
  accountDefaultLocationId,
  allowCustomLocation,
  opportunity,
  prefillClient,
}) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { trackingClient } = useTracking()
  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const userTimezone = useUserTimezone()
  const [createBooking, { isLoading: isSending }] = useCreateBookingMutation()
  const [updateBooking, { isLoading: isUpdating }] = useUpdateBookingMutation()
  const [createUserLocation, { isLoading: isSavingLocation }] = useCreateUserSchedulingLocationMutation()
  const copyRenderedEmail = useCopyRenderedEmail()
  const { isEmailConnected, isPushing, pushToDraft } = usePushBookingToDraft()
  const { byPurpose: templateByPurpose, isLoading: isTemplatesLoading, isError: isTemplatesError } = useSchedulingEmailTemplates()
  const { data: accountResponse } = useGetAccountQuery({ accountId })
  const [draftBooking, setDraftBooking] = useState<Booking | null>(null)
  const [titleEdited, setTitleEdited] = useState(false)
  const [notice, setNotice] = useState<SaveBarNotice | null>(null)
  const { show: showSupport } = useIntercom()

  const opportunitySuggestedDay = upcomingSuggestedDay(opportunity?.suggestedDate)

  const form = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    mode: 'onChange',
    shouldUnregister: false,
    // Prefill is loaded before this component mounts, so defaults initialize the form directly.
    defaultValues: {
      owner: authenticatedUser,
      eventType: prefillEventType ?? null,
      clients: opportunity?.clients ?? (prefillClient ? [prefillClient] : []),
      timeMode: 'specific-time',
      slot: null,
      suggestedDate: opportunitySuggestedDay ?? null,
      meetingMode: opportunity?.meetingMode ?? MeetingMode.VIRTUAL,
      location: opportunity?.location ?? null,
      note: '',
      title: '',
      invitees: [],
      timezone: '',
      customLocation: null,
    },
  })
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = form
  const { isStale, isDraftStale, capture: captureLinkBaseline, captureDraft: captureDraftBaseline } = useLinkBaseline(form)

  const owner = useWatch({ control, name: 'owner' })
  const eventType = useWatch({ control, name: 'eventType' })
  const clients = useWatch({ control, name: 'clients' })
  const timeMode = useWatch({ control, name: 'timeMode' })
  const slot = useWatch({ control, name: 'slot' })
  const suggestedDate = useWatch({ control, name: 'suggestedDate' })
  const meetingMode = useWatch({ control, name: 'meetingMode' })
  const note = useWatch({ control, name: 'note' })
  const locationRef = useWatch({ control, name: 'location' })
  const title = useWatch({ control, name: 'title' })
  const invitees = useWatch({ control, name: 'invitees' })
  const timezone = useWatch({ control, name: 'timezone' })
  const customLocation = useWatch({ control, name: 'customLocation' })
  const previewClients = useMemo(() => withDraftInviteUrls(clients, draftBooking), [clients, draftBooking])
  const validate = { shouldValidate: true, shouldDirty: true }

  const { state: linkState, onLinkShared } = useLinkShareState(isStale)

  const ownerId = owner?.id
  const ownerName = owner ? getUserDisplayName(owner) : ''
  const isBookingForOther = ownerId != null && ownerId !== authenticatedUser.id

  const { data: userLocations } = useGetUserSchedulingLocationsQuery(ownerId ? { accountId, userId: ownerId } : skipToken)
  const { data: userSettings, isFetching: isUserSettingsFetching } = useGetUserSchedulingSettingsQuery(ownerId ? { accountId, userId: ownerId } : skipToken)
  const { data: ownerEventTypes, isFetching: isOwnerEventTypesFetching } = useGetSchedulingEventTypesQuery(ownerId ? { accountId, userId: ownerId } : skipToken)

  const isOwnerCalendarBookable = userSettings ? hasBookableCalendar(userSettings) : false
  const isOwnerPlatformBroken = userSettings ? isMeetingPlatformBroken(userSettings) : false
  const isOwnerSetupLoading = isUserSettingsFetching || isOwnerEventTypesFetching

  const incompleteReason = getIncompleteReason({ owner, eventType, clients, timeMode, slot, isOwnerCalendarBookable, isBookingForOther, ownerName })

  useEffect(() => {
    if (titleEdited) {
      return
    }
    setValue('title', suggestMeetingTitle(clients, ownerName, eventType))
  }, [clients, ownerName, eventType, titleEdited, setValue])

  const locations = [...accountLocations, ...(userLocations ?? [])]
  const selectedSavedLocation = locationRef?.id ? locations.find((location) => location.id === locationRef.id) : undefined
  const effectiveLocationTimezone = customLocation?.timeZone || selectedSavedLocation?.timeZone || ''
  const locationTimezoneLabel = effectiveLocationTimezone ? formatTimezoneLabel(effectiveLocationTimezone) : ''

  const isInPersonMode = meetingMode === MeetingMode.IN_PERSON
  const viewTimezone = resolveViewTimezone({
    isInPerson: isInPersonMode,
    locationTimezone: effectiveLocationTimezone,
    selectedTimezone: timezone,
    userTimezone,
  })

  useEffect(() => {
    if (isInPersonMode && effectiveLocationTimezone && effectiveLocationTimezone !== timezone) {
      setValue('timezone', effectiveLocationTimezone)
    }
  }, [isInPersonMode, effectiveLocationTimezone, timezone, setValue])

  const handleOwnerChange = (nextOwner: User) => {
    if (nextOwner.id === owner?.id) {
      return
    }
    setValue('owner', nextOwner, validate)
    // The owner determines the offered event types, locations and availability, so start those over
    setValue('eventType', null, validate)
    setValue('slot', null, validate)
    setValue('location', null, validate)
    setValue('customLocation', null, validate)
  }

  const handleEventTypeChange = (nextEventType: SchedulingEventType) => {
    const nextRef = nextEventType.location
    setValue('eventType', nextEventType, validate)
    setValue('slot', null, validate)
    setValue('meetingMode', nextEventType.meetingMode, validate)
    setValue(
      'location',
      nextRef ? { kind: nextRef.kind, id: resolveLocationPreviewId(nextRef, accountDefaultLocationId, userSettings?.defaults.locationId) } : null,
      validate,
    )
    setValue('customLocation', null, validate)
  }

  const resolveLocationFields = async (data: ScheduleFormValues): Promise<Pick<CreateBookingPayload, 'location'> | null> => {
    if (data.meetingMode === MeetingMode.IN_PERSON && data.customLocation && data.owner) {
      const saved = await createUserLocation({ accountId, userId: data.owner.id, input: data.customLocation })
      if ('error' in saved) {
        return null
      }
      return { location: { kind: LocationKind.FIXED, id: saved.data.id } }
    }
    return bookingLocationFields(data.meetingMode, data.location)
  }

  const runCreate = async (data: ScheduleFormValues, delivery: DeliveryMode): Promise<Booking | null> => {
    if (!data.eventType || !data.owner || !isOwnerCalendarBookable) {
      return null
    }
    const locationFields = await resolveLocationFields(data)
    if (!locationFields) {
      return null
    }
    const result = await createBooking({
      accountId,
      payload: {
        clients: data.clients,
        userId: data.owner.id,
        eventTypeId: data.eventType.id,
        ...locationFields,
        start: data.timeMode === 'specific-time' ? data.slot?.startAt : undefined,
        suggestedDate: data.timeMode === 'client-chooses' && data.suggestedDate ? toLocalISODate(data.suggestedDate) : undefined,
        note: data.note || undefined,
        title: titleEdited ? data.title.trim() || undefined : undefined,
        additionalInvitees: data.invitees.length > 0 ? data.invitees : undefined,
        opportunityId: opportunity?.bookingId,
        delivery,
      },
    })
    if ('error' in result) {
      if (opportunity) {
        setNotice(errorNotice('Scheduling from an opportunity is not supported yet.', false))
      }
      return null
    }
    return result.data
  }

  const runUpdateDraft = async (data: ScheduleFormValues, bookingId: string): Promise<Booking | null> => {
    if (!data.eventType || !data.owner) {
      return null
    }
    const locationFields = await resolveLocationFields(data)
    if (!locationFields) {
      return null
    }
    const result = await updateBooking({
      accountId,
      payload: {
        bookingId,
        eventTypeId: data.eventType.id,
        clients: data.clients,
        additionalInvitees: data.invitees.length > 0 ? data.invitees : undefined,
        ...locationFields,
        start: data.timeMode === 'specific-time' ? data.slot?.startAt : undefined,
        suggestedDate: data.timeMode === 'client-chooses' && data.suggestedDate ? toLocalISODate(data.suggestedDate) : undefined,
        note: data.note || undefined,
        title: titleEdited ? data.title.trim() || undefined : undefined,
      },
    })
    return 'error' in result ? null : result.data
  }

  const ensureDraftBooking = async (data: ScheduleFormValues): Promise<Booking | null> => {
    if (draftBooking && !isDraftStale) {
      return draftBooking
    }
    // A held draft is updated, never recreated — a diverged form must not leave a second booking behind.
    const booking = draftBooking ? await runUpdateDraft(data, draftBooking.bookingId) : await runCreate(data, DeliveryMode.DRAFT)
    if (booking) {
      setDraftBooking(booking)
      captureDraftBaseline()
    }
    return booking
  }

  const onSend = handleSubmit(async (data) => {
    setNotice(null)
    const booking = await runCreate(data, DeliveryMode.SEND)
    if (!booking) {
      if (!opportunity) {
        setNotice(data.timeMode === 'specific-time' ? saveBarNotices.sendInviteError() : saveBarNotices.sendEmailsError(data.clients.length))
      }
      return
    }
    trackingClient.track({
      eventName: TrackEvent.SCHEDULING_INVITATION_SENT,
      eventDetails: {
        bookingId: booking.bookingId,
        timeMode: data.timeMode,
        meetingMode: data.meetingMode,
        clientCount: data.clients.length,
        isBookingForOther,
        fromOpportunity: opportunity != null,
      },
    })
    dispatch(addToast({ variant: 'success', title: 'Invitation sent', messages: [`${booking.title ?? 'The booking'} is now awaiting the client's reply.`] }))
    navigate('/scheduling/waiting')
  })

  const onCopyLink = handleSubmit(async (data) => {
    setNotice(null)
    const booking = await ensureDraftBooking(data)
    if (!booking) {
      if (!opportunity) {
        setNotice(saveBarNotices.copyLinkError('booking'))
      }
      return
    }
    const links = collectClientInviteLinks(booking.clients)
    if (links.length === 0) {
      setNotice(errorNotice('The booking was saved, but it has no booking link — copy it from the Waiting tab.', false))
      return
    }
    try {
      await navigator.clipboard.writeText(formatClientInviteLinks(links))
    } catch (error) {
      Sentry.captureException(error)
      setNotice(errorNotice('Could not copy the link. The booking was saved — copy it from the Waiting tab.', false))
      return
    }
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_LINK_COPIED, eventDetails: { flow: 'schedule', bookingId: booking.bookingId } })
    captureLinkBaseline()
    onLinkShared()
    setNotice(saveBarNotices.linkCopied('booking', links.length))
  })

  const onUpdateBookingPage = handleSubmit(async (data) => {
    setNotice(null)
    if (!draftBooking) {
      return
    }
    const booking = await runUpdateDraft(data, draftBooking.bookingId)
    if (!booking) {
      setNotice(saveBarNotices.saveBookingError())
      return
    }
    setDraftBooking(booking)
    trackingClient.track({ eventName: TrackEvent.SCHEDULING_BOOKING_PAGE_UPDATED, eventDetails: { flow: 'schedule', bookingId: booking.bookingId } })
    captureLinkBaseline()
    onLinkShared()
    setNotice(saveBarNotices.bookingPageUpdated())
  })

  const onCopyEmail = (previewClient?: BookingClient) => {
    void handleSubmit(async (data) => {
      setNotice(null)
      if (isTemplatesLoading || isTemplatesError) {
        setNotice(errorNotice('Your email templates could not be loaded.'))
        return
      }
      const template = templateByPurpose(EmailTemplatePurpose.INVITATION_TO_BOOK)
      if (!template || !template.subject || !template.body) {
        setNotice(errorNotice('There is no email template for this email yet. Configure one in Setup.', false))
        return
      }
      const recipient = previewClient ?? data.clients[0]
      const booking = await ensureDraftBooking(data)
      if (!booking || !data.eventType || !recipient) {
        if (!booking && !opportunity) {
          setNotice(errorNotice('Something went wrong creating the email.'))
        }
        return
      }
      const link = clientInviteUrl(booking, recipient)
      if (!link) {
        setNotice(errorNotice('The booking was saved, but it has no booking link — copy it from the Waiting tab.', false))
        return
      }
      const context = buildEmailTemplateContext({
        eventType: data.eventType,
        client: recipient,
        owner: data.owner ?? undefined,
        note: data.note,
        meetingMode: data.meetingMode,
        location: data.customLocation ?? selectedSavedLocation ?? null,
        bookingLink: link,
        firmName: accountResponse?.data.name,
      })
      const outcome = await copyRenderedEmail(renderEmailSubject(template.subject, context), renderEmailHtmlBody(template.body, context))
      if (outcome === 'copied') {
        trackingClient.track({ eventName: TrackEvent.SCHEDULING_INVITATION_EMAIL_COPIED, eventDetails: { flow: 'schedule', bookingId: booking.bookingId } })
        captureLinkBaseline()
        onLinkShared()
        setNotice(saveBarNotices.emailCopied())
      } else {
        setNotice(errorNotice('Could not copy the email.'))
      }
    })()
  }

  const onPushToDraft = handleSubmit(async (data) => {
    setNotice(null)
    const booking = await ensureDraftBooking(data)
    if (!booking || !data.eventType) {
      if (!booking && !opportunity) {
        setNotice(saveBarNotices.pushToDraftError(data.clients.length))
      }
      return
    }
    const result = await pushToDraft({
      booking,
      eventType: data.eventType,
      owner: data.owner ?? undefined,
      note: data.note,
      purpose: EmailTemplatePurpose.INVITATION_TO_BOOK,
      location: data.customLocation ?? selectedSavedLocation ?? null,
    })
    if (result.outcome === 'pushed') {
      trackingClient.track({
        eventName: TrackEvent.SCHEDULING_INVITATION_PUSHED_TO_DRAFT,
        eventDetails: { flow: 'schedule', drafts: result.created, bookingId: booking.bookingId },
      })
      setNotice(result.created < result.total ? saveBarNotices.partialDrafts(result.created, result.total) : saveBarNotices.pushedToDraft(result.created))
    } else if (result.outcome === 'backend-error') {
      setNotice(saveBarNotices.pushToDraftError(data.clients.length))
    } else if (result.reason === 'no-template') {
      setNotice(errorNotice('The booking was saved, but there is no email template for this email yet. Configure one in Setup.', false))
    } else if (result.reason === 'no-recipients') {
      setNotice(errorNotice(noDraftRecipientsMessage('The booking was saved'), false))
    }
  })

  return (
    <div className='d-flex flex-column min-vh-100'>
      <div className='flex-grow-1 zocks-pb-10.0'>
        <Row>
          <Col lg={6} className='d-flex flex-column gap-4'>
            {isOwnerPlatformBroken ? <MeetingPlatformWarning /> : null}
            <form id={FORM_ID} className='d-flex flex-column gap-4' onSubmit={onSend}>
              <div className='d-flex flex-column gap-3 p-3 border zocks-border-color-gray-200 zocks-border-1.0'>
                <OwnerSelect onOwnerChange={handleOwnerChange} disabled={draftBooking !== null} />
                {isOwnerSetupLoading ? (
                  <div className='paragraph-small zocks-text-gray-500'>Checking {isBookingForOther ? `${ownerName}'s` : 'your'} scheduling setup…</div>
                ) : userSettings && !isOwnerCalendarBookable ? (
                  <div className='d-flex align-items-center zocks-gap-0.25 paragraph-small zocks-text-warning-dark'>
                    <i className='bi bi-exclamation-triangle-fill' aria-hidden='true' />
                    {isBookingForOther
                      ? `${ownerName}'s calendar isn't ready for booking, so you can't book on their behalf yet.`
                      : 'Connect or reconnect your calendar to book meetings.'}
                  </div>
                ) : null}
                <EventTypeSelect eventTypes={ownerEventTypes ?? []} selectedEventTypeId={eventType?.id ?? null} onEventTypeChange={handleEventTypeChange} />
                <ClientSearch
                  clients={clients}
                  ownerId={isBookingForOther ? ownerId : undefined}
                  onClientsChange={(value) => setValue('clients', value, validate)}
                />
                <InviteOthers invitees={invitees} onInviteesChange={(value) => setValue('invitees', value, validate)} />
              </div>
              {eventType ? (
                <>
                  {meetingMode === MeetingMode.IN_PERSON ? (
                    <LocationSelect
                      eventType={eventType}
                      accountDefaultLocationId={accountDefaultLocationId}
                      userDefaultLocationId={userSettings?.defaults.locationId}
                      accountLocations={accountLocations}
                      userLocations={userLocations ?? []}
                      ownerUserId={ownerId ?? ''}
                      ownerFirstName={(owner?.name ?? '').split(' ')[0]}
                      isSchedulingForSelf={ownerId === authenticatedUser.id}
                      location={locationRef}
                      customLocation={customLocation}
                      allowCustomLocation={allowCustomLocation}
                      onChange={(next) => {
                        if (next.location !== undefined) {
                          setValue('location', next.location, validate)
                        }
                        if (next.customLocation !== undefined) {
                          setValue('customLocation', next.customLocation, validate)
                        }
                      }}
                    />
                  ) : null}
                  <SetTimeSection
                    mode={timeMode}
                    eventTypeId={eventType.id}
                    userId={ownerId}
                    meetingMode={meetingMode}
                    timezone={viewTimezone}
                    locationTimezoneLabel={locationTimezoneLabel}
                    selectedSlot={slot}
                    suggestedDate={suggestedDate}
                    initialDay={opportunitySuggestedDay}
                    onModeChange={(value) => setValue('timeMode', value, validate)}
                    onSlotChange={(value) => setValue('slot', value, validate)}
                    onSuggestedDateChange={(value) => setValue('suggestedDate', value, validate)}
                    onTimezoneChange={(value) => setValue('timezone', value)}
                  />
                  <MeetingNameField
                    title={title}
                    onTitleChange={(value) => {
                      setValue('title', value, validate)
                      setTitleEdited(true)
                    }}
                  />
                </>
              ) : null}
            </form>
          </Col>
          <Col lg={6}>
            <SchedulePreviewColumn
              eventType={eventType ?? undefined}
              mode={timeMode}
              emptyState={
                <div className='d-flex align-items-center justify-content-center text-center p-4 border zocks-border-color-gray-200 zocks-border-1.0 paragraph-medium zocks-text-gray-500'>
                  Fill in the details to see a preview of what the client gets.
                </div>
              }
              emailPurpose={EmailTemplatePurpose.INVITATION_TO_BOOK}
              owner={owner ?? undefined}
              ownerName={ownerName}
              invitees={invitees}
              meetingMode={meetingMode}
              meetingPlatform={userSettings?.integrations.selectedMeetingPlatform}
              joinLinkBroken={isOwnerPlatformBroken}
              slot={slot}
              title={title}
              timeZone={viewTimezone}
              timezoneLabel={isInPersonMode ? locationTimezoneLabel : viewTimezone ? formatTimezoneLabel(viewTimezone) : ''}
              location={customLocation ?? selectedSavedLocation ?? null}
              note={note}
              onNoteChange={(value) => setValue('note', value, validate)}
              clients={previewClients}
              onCopyEmail={onCopyEmail}
              copyEmailDisabled={!isValid || !isOwnerCalendarBookable || isSending || isSavingLocation || isUpdating || linkState === 'stale'}
              copyEmailDisabledReason={linkState === 'stale' ? STALE_TITLE : incompleteReason}
            />
          </Col>
        </Row>
      </div>
      <ScheduleSaveBar
        formId={FORM_ID}
        mode={timeMode}
        isValid={isValid && isOwnerCalendarBookable}
        isSubmitting={isSending || isSavingLocation || isUpdating || isPushing}
        linkState={linkState}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        incompleteReason={incompleteReason}
        onContactSupport={showSupport}
        onCopyLink={onCopyLink}
        onUpdateBookingPage={onUpdateBookingPage}
        onPushToDraft={onPushToDraft}
        pushToDraftDisabled={!isEmailConnected}
        pushToDraftDisabledReason='Connect your email account to push drafts.'
        submitDisabled={draftBooking !== null}
        submitDisabledReason='The booking link is already shared — clients book through it.'
      />
    </div>
  )
}

export const ScheduleMeetingFlow: FC = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const opportunityId = searchParams.get('opportunityId')
  const prefillContactId = searchParams.get('contactId')

  const authenticatedUser = useAppSelector(selectSafeAuthenticatedUser)
  const accountId = authenticatedUser.accountId
  const ownEventTypesQuery = useGetSchedulingEventTypesQuery({ accountId, userId: authenticatedUser.id })
  const { data: accountSettings } = useGetAccountSchedulingSettingsQuery({ accountId })
  const { data: locations } = useGetSchedulingLocationsQuery({ accountId })
  const opportunityQuery = useGetBookingByIdQuery(opportunityId ? { accountId, bookingId: opportunityId } : skipToken)
  const prefillContactQuery = useGetExternalUserByIdQuery(prefillContactId ? { accountId, userId: prefillContactId } : skipToken)

  useDocumentTitle('Scheduling | Zocks')

  // The form initializes its state from the prefill data, so wait for everything it needs before
  // mounting it. Key off the queries' loading flags (not data presence) so an errored lookup falls
  // through to an empty form instead of leaving the spinner up forever.
  const isPrefillPending = Boolean(
    ownEventTypesQuery.isLoading || (opportunityId && opportunityQuery.isLoading) || (prefillContactId && prefillContactQuery.isLoading),
  )

  const opportunityEventTypeId = opportunityQuery.data?.eventTypeId
  const prefillEventType = opportunityEventTypeId ? ownEventTypesQuery.data?.find((candidate) => candidate.id === opportunityEventTypeId) : undefined

  const prefillUser = prefillContactQuery.data?.data
  const prefillClient: BookingClient | undefined = prefillUser
    ? { contactId: prefillUser.id, firstName: prefillUser.name, email: prefillUser.email ?? '' }
    : undefined

  return (
    <Container>
      <SchedulingFlowHeader title='Schedule a meeting' onBack={() => navigate('/scheduling/requests')} onClose={() => navigate('/scheduling/requests')} />
      {isPrefillPending ? (
        <div className='d-flex justify-content-center py-5'>
          <Spinner />
        </div>
      ) : (
        <ScheduleMeetingForm
          prefillEventType={prefillEventType}
          accountLocations={locations ?? []}
          accountDefaultLocationId={accountSettings?.defaults.locationId}
          allowCustomLocation={canManageOwnLocations(accountSettings?.userControls)}
          opportunity={opportunityQuery.data}
          prefillClient={prefillClient}
        />
      )}
    </Container>
  )
}
